import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

import { Spinner } from './spinner';

/**
 * Klaret Button (handoff §10.1). One primary action per view; everything else
 * steps down. Radius r2 (6px) at every size, min-width 64px. Focus is the global
 * 2px INK outline (globals.css) — never an accent ring. Colours are role tokens
 * only; `--tvl-on-accent` (never #fff) is the on-accent text.
 *
 * `asChild` renders the single child with the button's classes/props merged in
 * (e.g. router <Link asChild>), via a local merge-slot — no Radix dependency.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none',
    'min-w-16 rounded-[var(--tvl-r2)] font-medium',
    'transition-[background-color,color] duration-[var(--tvl-d-fast)] ease-[var(--tvl-ease-out)]',
    'disabled:pointer-events-none disabled:text-[var(--tvl-ink-disabled)] disabled:bg-[var(--tvl-wash-pressed)]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--tvl-accent)] text-[var(--tvl-on-accent)] hover:bg-[var(--tvl-accent-hover)] active:bg-[var(--tvl-accent-pressed)]',
        secondary:
          'bg-transparent text-[var(--tvl-ink)] border border-[var(--tvl-border-input)] hover:bg-[var(--tvl-wash-hover)] active:bg-[var(--tvl-wash-pressed)]',
        ghost:
          'bg-transparent text-[var(--tvl-ink)] hover:bg-[var(--tvl-wash-hover)] active:bg-[var(--tvl-wash-pressed)]',
        tertiary:
          'bg-transparent text-[var(--tvl-accent)] underline-offset-4 hover:bg-[var(--tvl-wash-hover)] hover:underline px-0 min-w-0',
        danger:
          'bg-[var(--tvl-danger)] text-[var(--tvl-on-accent)] hover:brightness-[0.92] active:brightness-[0.85]',
        // legacy alias kept for existing call sites; same as tertiary
        link: 'bg-transparent text-[var(--tvl-accent)] underline-offset-4 hover:underline px-0 min-w-0',
      },
      size: {
        sm: 'h-7 px-2.5 text-[13px] leading-[18px]',
        md: 'h-9 px-3.5 text-sm',
        lg: 'h-11 px-[18px] text-sm',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

/** Minimal asChild slot — merges className + props onto a single child element. */
const Slot = React.forwardRef<HTMLElement, { children: React.ReactNode } & Record<string, unknown>>(
  ({ children, ...slotProps }, ref) => {
    if (!React.isValidElement(children)) return null;
    const child = children as React.ReactElement<Record<string, unknown>>;
    return React.cloneElement(child, {
      ...slotProps,
      ...child.props,
      className: cn(slotProps.className as string, child.props.className as string),
      ref,
    } as Record<string, unknown>);
  },
);
Slot.displayName = 'Slot';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = cn(buttonVariants({ variant, size, fullWidth }), className);
    const content = (
      <>
        {loading ? (
          <Spinner size={size === 'lg' ? 'sm' : 'xs'} />
        ) : leftIcon ? (
          <span className="inline-flex shrink-0">{leftIcon}</span>
        ) : null}
        {children ? <span className="inline-flex">{children}</span> : null}
        {!loading && rightIcon ? (
          <span className="inline-flex shrink-0">{rightIcon}</span>
        ) : null}
      </>
    );

    if (asChild) {
      return (
        <Slot ref={ref} className={classes} aria-busy={loading || undefined} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = 'Button';
